import {Menu, MenuItem} from "navigation-common/types/navigation-types";
import {TreeData, TreeItem} from "@atlaskit/tree";
import {navigationHelperService} from "./NavigationHelperService";
import {ItemId} from "@atlaskit/tree/types";

class TreeHelperService {

    public transformTreeDataToMenu(menu: Menu, treeData: TreeData): Menu {
        let newMenuItems: Array<MenuItem> = [];

        treeData.items[treeData.rootId].children.forEach((lvl1Id, i) => {
            const lvl1TreeItem = treeData.items[lvl1Id];
            let lvl1MenuItem = this.copyMenuItemWithoutSubMenus(lvl1TreeItem.data);
            (lvl1TreeItem.data as MenuItem).key = navigationHelperService.buildKeyAtIndex(1, `${i}.0.0`);

            if (lvl1TreeItem.hasChildren) {
                lvl1MenuItem.subMenuItems = [];

                lvl1TreeItem.children.forEach((lvl2Id, j) => {
                    const lvl2TreeItem = treeData.items[lvl2Id];
                    let lvl2MenuItem = this.copyMenuItemWithoutSubMenus(lvl2TreeItem.data);
                    (lvl2TreeItem.data as MenuItem).key = navigationHelperService.buildKeyAtIndex(2, `${i+1}.${j}.0`);

                    if (lvl2TreeItem.hasChildren) {
                        lvl2MenuItem.subMenuItems = [];

                        lvl2TreeItem.children.forEach((lvl3Id, k) => {
                            const lvl3TreeItem = treeData.items[lvl3Id];
                            const lvl3MenuItem = this.copyMenuItemWithoutSubMenus(lvl3TreeItem.data);
                            (lvl3TreeItem.data as MenuItem).key = navigationHelperService.buildKeyAtIndex(3, `${i+1}.${j+1}.${k}`);

                            lvl2MenuItem.subMenuItems?.push(lvl3MenuItem);
                        })
                    }

                    lvl1MenuItem.subMenuItems?.push(lvl2MenuItem);
                })
            }

            newMenuItems.push(lvl1MenuItem);
        });

        menu.menuItems = newMenuItems;
        navigationHelperService.resetAllKeys(menu);
        return menu;
    }

    private copyMenuItemWithoutSubMenus(menuItem: MenuItem): MenuItem {
        return {...menuItem, subMenuItems: undefined};
    }

    private transformToExpandedStateMap(treeData: TreeData): Map<string, boolean> {
        const treeExpandedState = new Map<string, boolean>();
        Object.values(treeData?.items ?? {}).forEach((item) => {
            if (item.data) {
                treeExpandedState.set((item.data as MenuItem).key, item.isExpanded ?? false);
            }
        });

        return treeExpandedState;
    }

    public transformMenuToTreeData(menu: Menu | undefined, oldTreeData: TreeData | undefined): TreeData | undefined {
        if (menu === undefined) {
            return undefined;
        }

        const treeExpandedState = oldTreeData ? this.transformToExpandedStateMap(oldTreeData) : new Map<string, boolean>();

        const rootTreeItem: TreeItem = {
            id: 1,
            children: [],
            hasChildren: true,
            isExpanded: false,
        }

        let allChildren: TreeItem[] = [rootTreeItem];

        menu.menuItems.forEach((menuItem) => {
            let treeItemLvl1: TreeItem = this.mapMenuItemToTreeItem(menuItem, treeExpandedState.get(menuItem.key));
            rootTreeItem.children.push(treeItemLvl1.id);
            allChildren.push(treeItemLvl1);

            if (menuItem.subMenuItems && menuItem.subMenuItems.length > 0) {
                menuItem.subMenuItems.forEach((lvl2MenuItem) => {
                    const treeItemLvl2 = this.mapMenuItemToTreeItem(lvl2MenuItem, treeExpandedState.get(lvl2MenuItem.key));
                    treeItemLvl1.children.push(treeItemLvl2.id);
                    allChildren.push(treeItemLvl2);

                    if (lvl2MenuItem.subMenuItems && lvl2MenuItem.subMenuItems.length > 0) {
                        lvl2MenuItem.subMenuItems.forEach((lvl3MenuItem) => {
                            const treeItemLvl3 = this.mapMenuItemToTreeItem(lvl3MenuItem, treeExpandedState.get(lvl3MenuItem.key));
                            treeItemLvl2.children.push(treeItemLvl3.id);
                            allChildren.push(treeItemLvl3);
                        })
                    }
                })
            }

            allChildren.push(treeItemLvl1);
        })

        const transformedData = Object.fromEntries(allChildren.map(item => [item.id, item]));

        return {
            rootId: 1,
            items: transformedData
        };
    }

    private mapMenuItemToTreeItem(menuItem: MenuItem, itemIsExpanded: boolean | undefined) {
        const hasChildren = menuItem.subMenuItems && menuItem.subMenuItems.length > 0;
        return {
            id: this.generateUUID(),
            children: [],
            isExpanded: !!(hasChildren && itemIsExpanded),
            isChildrenLoading: false,
            hasChildren,
            data: menuItem
        } as TreeItem;
    }

    public getTreeItemByMenuItemKey(menuItem: MenuItem, treeData: TreeData): TreeItem | undefined {
        let foundTreeItem: TreeItem | undefined;

        Object.values(treeData.items).forEach((treeItem) => {
            if ((treeItem.data as MenuItem)?.key === menuItem.key) {
                foundTreeItem = treeItem;
            }
        });

        return foundTreeItem;
    }

    // copied from https://stackoverflow.com/a/8809472/6291294
    private generateUUID(): string {
        let d = new Date().getTime();//Timestamp
        let d2 = ((typeof performance !== 'undefined') && performance.now && (performance.now() * 1000)) || 0;//Time in microseconds since page-load or 0 if unsupported
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
            let r = Math.random() * 16;//random number between 0 and 16
            if (d > 0) {//Use timestamp until depleted
                r = (d + r) % 16 | 0;
                d = Math.floor(d / 16);
            } else {//Use microseconds since page-load if supported
                r = (d2 + r) % 16 | 0;
                d2 = Math.floor(d2 / 16);
            }
            return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
        });
    }

    public getMaxDepthOfTreeItemById({id, tree}: { id: ItemId, tree: TreeData }): number {
        let depth = 0;
        const lvl1menuItem = tree.items[id].data as MenuItem;

        if (lvl1menuItem.subMenuItems && lvl1menuItem.subMenuItems.length > 0) {
            depth++;
            lvl1menuItem.subMenuItems.forEach(lvl2menuItem => {
                if (lvl2menuItem.subMenuItems && lvl2menuItem.subMenuItems.length > 0) {
                    depth++;
                }
            });
        }

        return depth;
    }
}

export const treeHelperService = new TreeHelperService();
